import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Sparkles, Eye, Heart, Star, MapPin, ArrowRight, Calendar, Compass, Hand, Gem, BookOpen
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import LatestBlogTeaser from "./LatestBlogTeaser";
import { ROBOTS_META_CONTENT } from "../lib/indexing";

const SITE_URL = "https://www.bilan2vie.fr";
const PHOTO_URL = "https://customer-assets.emergentagent.com/job_spirituel-wellness/artifacts/wikfpu6u_Photo%20de%20profil%20YouTube%202.PNG";

const FORMATIONS = [
  { icon: Hand, name: "Pranic Healing", desc: "Soin énergétique ancestral travaillant sur les chakras et le corps éthérique." },
  { icon: Sparkles, name: "Reiki", desc: "Canalisation de l'énergie universelle pour rééquilibrer les corps subtils." },
  { icon: Gem, name: "Lithothérapie", desc: "Guérison par les pierres et les cristaux, complément aux soins énergétiques." },
  { icon: BookOpen, name: "Tarots & Oracles", desc: "Lecture intuitive et précise des cartes pour révéler les messages." },
];

const APPROACH = [
  { icon: Heart, title: "Empathie", color: "#D926A9", desc: "À ton écoute, sans jugement. Je crée un espace sûr pour libérer ce qui doit l'être." },
  { icon: Eye, title: "Médiumnité", color: "#00E5FF", desc: "Précis et sans complaisance. Je te transmets les messages tels qu'ils me sont délivrés." },
  { icon: Compass, title: "Écoute & Sagesse", color: "#9D4CDD", desc: "Ma sensibilité à l'humain enrichit chaque séance d'une dimension profonde et bienveillante." },
];

export default function QuiSuisJePage() {
  const navigate = useNavigate();
  return (
    <main className="pt-24 min-h-screen overflow-x-hidden" data-testid="qui-suis-je-page">
      <Helmet>
        <title>Qui suis-je ? — Guillaume, Médium & Praticien Pranic Healing à Montélimar | Bilan 2 Vie</title>
        <meta name="description" content="Originaire de l'île Maurice, Guillaume est médium, voyant et praticien Pranic Healing à Montélimar. Découvre son parcours, ses formations et son approche unique mêlant spiritualité, médiumnité et écoute bienveillante." />
        <meta name="keywords" content="Guillaume médium Montélimar, voyant Drôme, praticien Pranic Healing, médium île Maurice, Bilan de Vie, parcours spirituel, voyance médiumnité guidance" />
        <meta name="robots" content={ROBOTS_META_CONTENT} />
        <link rel="canonical" href={`${SITE_URL}/qui-suis-je`} />
        <meta property="og:title" content="Guillaume — Médium & Praticien Pranic Healing à Montélimar" />
        <meta property="og:description" content="Découvre le parcours et l'approche d'un médium aux racines mauriciennes installé à Montélimar." />
        <meta property="og:image" content={PHOTO_URL} />
        <meta property="og:type" content="profile" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Guillaume",
          "givenName": "Guillaume",
          "image": PHOTO_URL,
          "jobTitle": "Médium, Voyant & Praticien Pranic Healing",
          "description": "Médium et praticien Pranic Healing à Montélimar, originaire de l'île Maurice. Spécialiste du Bilan de Vie et des soins énergétiques.",
          "url": `${SITE_URL}/qui-suis-je`,
          "worksFor": { "@type": "LocalBusiness", "name": "Bilan 2 Vie", "address": { "@type": "PostalAddress", "addressLocality": "Montélimar", "postalCode": "26200", "addressCountry": "FR" } },
          "knowsAbout": ["Pranic Healing", "Reiki", "Lithothérapie", "Voyance", "Médiumnité", "Numérologie", "Bilan de Vie", "Tarot", "Oracles"]
        })}</script>
      </Helmet>

      {/* HERO */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        {/* Halos */}
        <div className="absolute -top-20 -left-32 w-[500px] h-[500px] rounded-full bg-[#D926A9]/15 blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -right-32 w-[500px] h-[500px] rounded-full bg-[#00E5FF]/10 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 bg-noise pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center relative z-10">
          {/* Photo avec halo neon */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="relative flex justify-center md:justify-end order-2 md:order-1"
          >
            <div className="relative w-[280px] sm:w-[340px] md:w-[400px] aspect-square">
              <div className="absolute -inset-3 rounded-full bg-gradient-to-br from-[#D926A9]/40 via-[#9D4CDD]/30 to-[#00E5FF]/30 blur-2xl animate-pulse-glow" />
              <img
                src={PHOTO_URL}
                alt="Guillaume — Médium à Montélimar"
                className="relative rounded-full w-full h-full object-cover border-2 border-white/15 shadow-[0_0_40px_rgba(217,38,169,0.35)]"
                data-testid="profile-photo"
              />
            </div>
          </motion.div>

          {/* Texte hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="order-1 md:order-2 text-center md:text-left"
          >
            <Badge className="bg-[#D926A9]/15 text-[#D926A9] border border-[#D926A9]/30 mb-4 text-xs uppercase tracking-[0.25em] px-3 py-1">
              Médium • Voyant • Praticien Pranic Healing
            </Badge>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-tight mb-5">
              Bienvenue,<br />
              <span className="bg-gradient-to-r from-[#D926A9] to-[#00E5FF] bg-clip-text text-transparent">
                je suis Guillaume
              </span>
            </h1>
            <p className="font-mystic text-2xl md:text-3xl text-[#00E5FF] glow-cyan mb-6">
              ma mission : t'éclairer
            </p>
            <p className="text-[#E2D5F8] text-lg leading-relaxed mb-8">
              Originaire de l'<strong className="text-[#F7F3FC]">île Maurice</strong> et issu d'une famille qui baigne dans la spiritualité depuis plusieurs générations, je me suis tourné vers ce chemin <strong className="text-[#F7F3FC]">depuis 2015</strong> et j'en ai fait mon <strong className="text-[#F7F3FC]">métier depuis 2021</strong> — à travers la <strong className="text-[#F7F3FC]">voyance</strong>, les <strong className="text-[#F7F3FC]">soins énergétiques</strong> et la <strong className="text-[#F7F3FC]">guidance</strong>.
            </p>
            <div className="flex flex-wrap items-center gap-3 justify-center md:justify-start text-sm text-[#B59ED1]">
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#D926A9]" /> Montélimar, Drôme</span>
              <span className="opacity-40">•</span>
              <span className="inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-[#00E5FF]" /> Spiritualité depuis 2015, métier depuis 2021</span>
              <span className="opacity-40">•</span>
              <span className="inline-flex items-center gap-1.5"><Star className="w-4 h-4 text-[#FFD700]" /> 4,9/5 sur 139 avis Google</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MON PARCOURS */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#9D4CDD]/30 to-transparent" />
        <div className="absolute -bottom-[10%] right-0 w-[600px] h-[600px] halo-corner-bl blur-3xl pointer-events-none" style={{ transform: 'scaleX(-1)' }} />
        <div className="absolute inset-0 bg-noise pointer-events-none" />

        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div className="text-center mb-10">
            <Badge className="bg-[#9D4CDD]/15 text-[#9D4CDD] border border-[#9D4CDD]/30 mb-3">Mon Parcours</Badge>
            <h2 className="font-serif text-3xl md:text-4xl">D'une île à un don, à un chemin</h2>
          </div>
          <div className="space-y-5 text-[#E2D5F8] leading-relaxed text-base md:text-lg">
            <p>
              Je me suis tourné vers la spiritualité en <strong className="text-[#F7F3FC]">2015</strong>, en commençant par apprivoiser mes facultés et mon magnétisme avec un <strong className="text-[#F7F3FC]">pendule</strong> et des <strong className="text-[#F7F3FC]">tirages de tarots</strong>. Très vite, ce qui n'était qu'une exploration personnelle est devenu une évidence : aider les autres à y voir plus clair. C'est en <strong className="text-[#F7F3FC]">2021</strong> que j'en ai fait mon <strong className="text-[#F7F3FC]">métier</strong>, pour me consacrer pleinement à cet accompagnement.
            </p>
            <p>
              J'ai profité de mes voyages à l'île Maurice pour me former à différentes pratiques et soins énergétiques (<strong className="text-[#F7F3FC]">Reiki & Pranic Healing</strong>) afin d'être capable de travailler sur les chakras et les corps énergétiques, dans le but de les rééquilibrer et de les réharmoniser.
            </p>
            <p>
              Depuis tout jeune, j'aime aider et conseiller les personnes qui me demandent, sur les chemins de la <strong className="text-[#F7F3FC]">spiritualité</strong> et de l'<strong className="text-[#F7F3FC]">écoute bienveillante</strong>, afin de les guider sur leur chemin de vie. De nature curieuse, je me suis également tourné vers la <strong className="text-[#F7F3FC]">lithothérapie</strong> (guérison par les pierres) et les <strong className="text-[#F7F3FC]">massages énergétiques</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* MON APPROCHE — 3 PILIERS */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00E5FF]/25 to-transparent" />
        <div className="absolute -top-[20%] left-0 w-[600px] h-[600px] halo-corner-tr blur-3xl pointer-events-none" style={{ transform: 'scaleX(-1)' }} />
        <div className="absolute inset-0 bg-noise pointer-events-none" />
        <span className="deco-star cyan" style={{ top: '20%', right: '12%' }} />
        <span className="deco-star magenta" style={{ bottom: '25%', left: '8%' }} />
        <span className="deco-star" style={{ top: '60%', right: '25%' }} />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/30 mb-3">Mon Approche</Badge>
            <h2 className="font-serif text-3xl md:text-4xl mb-3">
              Spiritualité <span className="text-[#D926A9] inline-block mx-1 align-middle text-2xl md:text-3xl" aria-hidden="true">✦</span> médiumnité <span className="text-[#D926A9] inline-block mx-1 align-middle text-2xl md:text-3xl" aria-hidden="true">✦</span> écoute
            </h2>
            <p className="text-[#B59ED1] max-w-2xl mx-auto">Empathique et connecté, je canalise les messages avec ma médiumnité et les cartes pour apporter des réponses claires et justes à tes questions sans complaisance.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {APPROACH.map((p, idx) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.12 }}
                  className="rounded-2xl bg-[#261B3B]/70 backdrop-blur-sm border border-white/10 p-7 hover:border-white/25 transition-all"
                  data-testid={`approach-${p.title.toLowerCase()}`}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${p.color}20`, border: `1px solid ${p.color}40` }}>
                    <Icon className="w-6 h-6" style={{ color: p.color, filter: `drop-shadow(0 0 6px ${p.color}60)` }} />
                  </div>
                  <h3 className="font-serif text-2xl mb-2 text-[#F7F3FC]">{p.title}</h3>
                  <p className="text-[#B59ED1] text-sm leading-relaxed">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FORMATIONS */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D926A9]/30 to-transparent" />
        <div className="absolute -bottom-[15%] left-0 w-[500px] h-[500px] halo-corner-bl blur-3xl pointer-events-none" />
        <div className="absolute inset-0 bg-noise pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-[#D926A9]/15 text-[#D926A9] border border-[#D926A9]/30 mb-3">Mes Formations</Badge>
            <h2 className="font-serif text-3xl md:text-4xl">Mes outils de travail</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {FORMATIONS.map((f, idx) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.name}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className="rounded-xl bg-[#1B132B]/80 border border-[#9D4CDD]/20 p-5 hover:border-[#D926A9]/50 transition-all text-center"
                  data-testid={`formation-${idx}`}
                >
                  <Icon className="w-7 h-7 mx-auto mb-3 text-[#00E5FF]" />
                  <h4 className="font-serif text-lg text-[#F7F3FC] mb-2">{f.name}</h4>
                  <p className="text-xs text-[#B59ED1] leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SPÉCIALITÉ : BILAN DE VIE */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[900px] max-h-[900px] halo-center-portal blur-3xl pointer-events-none" />
        <div className="absolute inset-0 bg-noise pointer-events-none" />
        <span className="deco-star" style={{ top: '15%', left: '10%' }} />
        <span className="deco-star cyan" style={{ bottom: '20%', right: '15%' }} />
        <span className="deco-star magenta" style={{ top: '40%', right: '8%' }} />

        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div className="rounded-3xl backdrop-blur-xl bg-[#261B3B]/40 border border-[#9D4CDD]/25 shadow-[0_0_60px_rgba(157,76,221,0.12)] p-8 md:p-12 text-center overflow-hidden relative">
            <div className="absolute top-5 left-5 text-2xl opacity-15 select-none">☾</div>
            <div className="absolute bottom-5 right-5 text-2xl opacity-15 select-none">✦</div>

            <Badge className="bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/30 mb-4">Ma signature</Badge>
            <h2 className="font-serif text-3xl md:text-4xl mb-3 text-[#F7F3FC]">Le Bilan de Vie</h2>
            <p className="font-mystic text-xl md:text-2xl text-[#00E5FF] glow-cyan mb-6">englober ce qui te traverse</p>
            <p className="text-[#E2D5F8] leading-relaxed mb-8 text-base md:text-lg">
              Le Bilan de Vie est une exploration profonde de ton chemin d'âme, de tes mémoires, de tes vies antérieures et de ton vécu actuel. Il met en lumière les blocages, peurs et schémas inconscients qui influencent ta vie aujourd'hui. L'objectif est de t'apporter de la clarté sur ton fonctionnement intérieur afin de mieux te comprendre et avancer avec plus de conscience.
            </p>
            <Button
              onClick={() => navigate('/booking', { state: { service: { name: 'Bilan de Vie' } } })}
              className="gradient-primary text-base px-8 py-6 rounded-full hover:shadow-[0_0_30px_rgba(217,38,169,0.5)] transition-all"
              data-testid="cta-bilan-de-vie"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Réserver un Bilan de Vie
            </Button>
          </div>
        </div>
      </section>

      {/* MA MISSION + CTA FINAL */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FFD700]/25 to-transparent" />
        <div className="absolute -top-[15%] right-[15%] w-[500px] h-[500px] halo-corner-tr blur-3xl pointer-events-none" />
        <div className="absolute -bottom-[15%] left-[10%] w-[500px] h-[500px] halo-corner-bl blur-3xl pointer-events-none" />
        <div className="absolute inset-0 bg-noise pointer-events-none" />

        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <Badge className="bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30 mb-4">Ma Mission de Vie</Badge>
          <h2 className="font-serif text-3xl md:text-5xl mb-6 leading-tight">
            T'aider grâce à mes <span className="bg-gradient-to-r from-[#D926A9] to-[#00E5FF] bg-clip-text text-transparent">facultés</span>, mes <span className="bg-gradient-to-r from-[#D926A9] to-[#00E5FF] bg-clip-text text-transparent">intuitions</span> et mon <span className="bg-gradient-to-r from-[#D926A9] to-[#00E5FF] bg-clip-text text-transparent">magnétisme</span>.
          </h2>
          <p className="text-[#B59ED1] mb-10 text-base md:text-lg">
            Grâce à mes oracles et tarots, je suis en mesure de t'offrir une séance de guidance ou de voyance sur tous les domaines : sentimental, professionnel, personnel, spirituel, vies antérieures...
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/booking')}
              className="gradient-primary text-lg px-8 py-6 rounded-full hover:shadow-[0_0_30px_rgba(217,38,169,0.5)] transition-all"
              data-testid="cta-final-booking"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Réserver une consultation
            </Button>
            <Link to="/services">
              <Button
                variant="outline"
                className="border-[#00E5FF]/40 text-[#00E5FF] text-lg px-8 py-6 rounded-full hover:bg-[#00E5FF]/10 w-full sm:w-auto"
                data-testid="cta-services"
              >
                Voir toutes mes prestations
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest blog teaser - internal SEO maillage */}
      <LatestBlogTeaser
        title="Pour aller plus loin"
        subtitle="Découvre mes articles sur la voyance, la médiumnité, les soins énergétiques et la lithothérapie."
        limit={3}
      />
    </main>
  );
}
